import { PeriodComparisonListItem, toPeriodComparisonListItems } from '@frontend/chart/components';
import { LendingOutboundPackageVolumeByProductMonthlyListChart } from '@frontend/chart/components';
import { type ReactNode, useMemo } from 'react';
import { LuTrendingDown, LuTrendingUp } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { StatisticsCard } from '../../shared';
import { takeRankedPeriodComparisonItems } from './take-ranked-period-comparison-items';
import { useLast60DaysLendingOutboundPackageVolumeByProductMonthlyListChart } from './use-last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart';

export interface Last60DaysLendingOutboundPackageVolumeByProductMonthlyListChartProps {
    accountId: string;
}

const PRODUCT_LIMIT = 5;

type ProductPopularityRow = { type: 'section'; label: ReactNode } | { type: 'item'; item: PeriodComparisonListItem; direction: 'up' | 'down' };

const trendClassName = (direction: 'up' | 'down'): string => (direction === 'up' ? 'text-status-success' : 'text-status-warning');

export const Last60DaysLendingOutboundPackageVolumeByProductMonthlyListChart = (
    props: Last60DaysLendingOutboundPackageVolumeByProductMonthlyListChartProps
) => {
    const { formatMessage } = useIntl();
    const { data, isLoading } = useLast60DaysLendingOutboundPackageVolumeByProductMonthlyListChart(props);

    const unknownLabel = formatMessage({
        id: 'Objects.UNKNOWN.DEFAULT',
        description: 'Label for unknown',
        defaultMessage: 'Unknown'
    });

    const { riserItems, fallerItems } = useMemo(() => {
        if (!data) {
            return { riserItems: [], fallerItems: [] };
        }

        const items = toPeriodComparisonListItems(data, unknownLabel);
        return {
            riserItems: takeRankedPeriodComparisonItems(items, 'up', PRODUCT_LIMIT),
            fallerItems: takeRankedPeriodComparisonItems(items, 'down', PRODUCT_LIMIT)
        };
    }, [data, unknownLabel]);

    const previousHeader = formatMessage({
        id: 'platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.previous-period',
        description: 'Column header for the prior month lend count in the Equipment popularity list on lending insights',
        defaultMessage: 'Previous'
    });

    const currentHeader = formatMessage({
        id: 'platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.current-period',
        description: 'Column header for the current month lend count in the Equipment popularity list on lending insights',
        defaultMessage: 'Current'
    });

    const changeHeader = formatMessage({
        id: 'platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.change',
        description: 'Column header for the month-over-month percentage change in the Equipment popularity list on the lending dashboard',
        defaultMessage: 'Change'
    });

    const equipmentHeader = formatMessage({
        id: 'Objects.EQUIPMENT.DEFAULT',
        description: 'Label for equipment',
        defaultMessage: 'Equipment'
    });

    const rows = useMemo((): ProductPopularityRow[] => {
        const result: ProductPopularityRow[] = [];

        if (riserItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.risers'
                        description='Section heading above the equipments with the highest month-over-month lend increase on the lending dashboard'
                        defaultMessage='Risers'
                    />
                )
            });
            result.push(...riserItems.map((item) => ({ type: 'item' as const, item, direction: 'up' as const })));
        }

        if (fallerItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.fallers'
                        description='Section heading above the equipments with the largest month-over-month lend decrease on the lending dashboard'
                        defaultMessage='Fallers'
                    />
                )
            });
            result.push(...fallerItems.map((item) => ({ type: 'item' as const, item, direction: 'down' as const })));
        }

        return result;
    }, [riserItems, fallerItems]);

    return (
        <StatisticsCard
            chartKey={LendingOutboundPackageVolumeByProductMonthlyListChart.definition.key}
            title={
                <FormattedMessage
                    id='platform.statistics.lending.last-60-days-lending-outbound-package-volume-by-product-monthly-list-chart.title'
                    description='Title of the lending dashboard list showing equipments with the largest month-over-month lend increase and decrease'
                    defaultMessage='Equipment popularity'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.statistics.pudo.last-60-days-pudo-timer-duration-metric-chart-card.subtitle'
                    description='Subtitle on PuDo dashboard cards comparing average drop-off or pick-up time to the previous month'
                    defaultMessage='Compared to last month'
                />
            }
            isLoading={isLoading}
            hasData={riserItems.length > 0 || fallerItems.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-2 border-b text-base font-medium'>
                    <div className='min-w-0 flex-1 px-4 py-3'>{equipmentHeader}</div>
                    <div className='w-[72px] shrink-0 px-2 py-3 text-right'>{previousHeader}</div>
                    <div className='w-[72px] shrink-0 px-2 py-3 text-right'>{currentHeader}</div>
                    <div className='w-[88px] shrink-0 px-2 py-3 text-right'>{changeHeader}</div>
                </div>
                {rows.map((row, index) =>
                    row.type === 'section' ? (
                        <div
                            key={`section-${index}`}
                            className='border-border-default text-foreground-default border-b px-4 py-3 text-base font-semibold'>
                            {row.label}
                        </div>
                    ) : (
                        <div
                            key={row.item.key}
                            className='border-border-default flex h-11 items-center gap-2 border-b'>
                            <div className='text-foreground-default flex min-w-0 flex-1 items-center px-4 text-sm font-medium'>{row.item.name}</div>
                            <div className='text-foreground-default flex w-[72px] shrink-0 justify-end px-2 text-sm tabular-nums'>
                                {row.item.previous ?? '—'}
                            </div>
                            <div className='text-foreground-default flex w-[72px] shrink-0 justify-end px-2 text-sm tabular-nums'>{row.item.value}</div>
                            <div
                                className={`flex w-[88px] shrink-0 items-center justify-end gap-1 pe-2 text-sm font-semibold ${trendClassName(row.direction)}`}>
                                {row.direction === 'up' ? <LuTrendingUp className='size-5' /> : <LuTrendingDown className='size-5' />}
                                {row.item.percentChange}%
                            </div>
                        </div>
                    )
                )}
            </div>
        </StatisticsCard>
    );
};
